import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const { width, height } = Dimensions.get('window');

export default function ProjectManagementScreen({ navigation }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Project statuses
  const [projectStatuses] = useState([
    'all', 'planning', 'in-progress', 'on-hold', 'completed', 'cancelled'
  ]);
const JOBENG_API_CONFIG = {
  BASE_URL: 'https://api.jobeng.ai',
  TIMEOUT: 30000,
  MAX_RETRIES: 3,
};
  // Project priorities
  const [projectPriorities] = useState([
    'all', 'low', 'medium', 'high', 'critical'
  ]);

  // Projects data
  const [projects, setProjects] = useState([
    {
      id: '1',
      name: 'Enterprise CRM Implementation',
      description: 'Complete CRM system implementation for TechCorp Ltd including data migration, customization, and training',
      client: 'TechCorp Ltd',
      status: 'in-progress',
      priority: 'high',
      progress: 65,
      budget: 285000.00,
      spent: 185250.00,
      remaining: 99750.00,
      currency: 'GBP',
      startDate: '2024-01-01T09:00:00Z',
      endDate: '2024-06-30T17:00:00Z',
      createdDate: '2023-12-15T10:00:00Z',
      updatedDate: '2024-01-18T14:30:00Z',
      projectManager: 'Sarah Johnson',
      teamSize: 8,
      category: 'Software Implementation',
      tags: ['crm', 'enterprise', 'implementation', 'data-migration'],
      milestones: [
        { 
          id: '1', 
          name: 'Requirements Gathering', 
          status: 'completed', 
          dueDate: '2024-01-15T17:00:00Z',
          completedDate: '2024-01-14T16:30:00Z',
          progress: 100 
        },
        { 
          id: '2', 
          name: 'System Setup & Configuration', 
          status: 'completed', 
          dueDate: '2024-02-15T17:00:00Z',
          completedDate: '2024-02-12T15:45:00Z',
          progress: 100 
        },
        { 
          id: '3', 
          name: 'Data Migration', 
          status: 'in-progress', 
          dueDate: '2024-03-15T17:00:00Z',
          completedDate: null,
          progress: 75 
        },
        { 
          id: '4', 
          name: 'User Training', 
          status: 'pending', 
          dueDate: '2024-05-15T17:00:00Z',
          completedDate: null,
          progress: 0 
        },
        { 
          id: '5', 
          name: 'Go-Live & Support', 
          status: 'pending', 
          dueDate: '2024-06-30T17:00:00Z',
          completedDate: null,
          progress: 0 
        }
      ],
      teamMembers: [
        { name: 'Sarah Johnson', role: 'Project Manager', avatar: 'SJ' },
        { name: 'Mike Chen', role: 'Lead Developer', avatar: 'MC' },
        { name: 'Emma Wilson', role: 'Data Analyst', avatar: 'EW' },
        { name: 'David Brown', role: 'System Admin', avatar: 'DB' },
        { name: 'Lisa Chen', role: 'UX Designer', avatar: 'LC' },
        { name: 'Tom Wilson', role: 'QA Engineer', avatar: 'TW' },
        { name: 'Anna Lee', role: 'Business Analyst', avatar: 'AL' },
        { name: 'Chris Brown', role: 'Developer', avatar: 'CB' }
      ],
      risks: [
        { 
          id: '1', 
          description: 'Data migration complexity higher than expected', 
          impact: 'medium', 
          probability: 'high',
          mitigation: 'Additional data analysis and testing phases added'
        },
        { 
          id: '2', 
          description: 'User adoption resistance', 
          impact: 'high', 
          probability: 'medium',
          mitigation: 'Enhanced training program and change management'
        }
      ],
      recentActivities: [
        { 
          id: '1', 
          type: 'milestone', 
          description: 'Data Migration milestone 75% complete', 
          date: '2024-01-18T14:30:00Z',
          user: 'Mike Chen'
        },
        { 
          id: '2', 
          type: 'update', 
          description: 'Budget review completed - on track', 
          date: '2024-01-17T11:20:00Z',
          user: 'Sarah Johnson'
        },
        { 
          id: '3', 
          type: 'meeting', 
          description: 'Weekly team standup completed', 
          date: '2024-01-16T10:00:00Z',
          user: 'Sarah Johnson'
        }
      ]
    },
    {
      id: '2',
      name: 'Office Building Construction',
      description: 'Modern office building construction project with sustainable design and smart building features',
      client: 'Green Builders Ltd',
      status: 'in-progress',
      priority: 'high',
      progress: 45,
      budget: 125000.00,
      spent: 56250.00,
      remaining: 68750.00,
      currency: 'GBP',
      startDate: '2023-11-01T09:00:00Z',
      endDate: '2024-08-31T17:00:00Z',
      createdDate: '2023-10-15T10:00:00Z',
      updatedDate: '2024-01-17T16:45:00Z',
      projectManager: 'David Brown',
      teamSize: 12,
      category: 'Construction',
      tags: ['construction', 'sustainable', 'office', 'smart-building'],
      milestones: [
        { 
          id: '1', 
          name: 'Foundation & Structure', 
          status: 'completed', 
          dueDate: '2024-01-31T17:00:00Z',
          completedDate: '2024-01-28T16:00:00Z',
          progress: 100 
        },
        { 
          id: '2', 
          name: 'Electrical & Plumbing', 
          status: 'in-progress', 
          dueDate: '2024-03-31T17:00:00Z',
          completedDate: null,
          progress: 60 
        },
        { 
          id: '3', 
          name: 'Interior Finishing', 
          status: 'pending', 
          dueDate: '2024-06-30T17:00:00Z',
          completedDate: null,
          progress: 0 
        },
        { 
          id: '4', 
          name: 'Final Inspection', 
          status: 'pending', 
          dueDate: '2024-08-31T17:00:00Z',
          completedDate: null,
          progress: 0 
        }
      ],
      teamMembers: [
        { name: 'David Brown', role: 'Project Manager', avatar: 'DB' },
        { name: 'John Smith', role: 'Site Supervisor', avatar: 'JS' },
        { name: 'Rachel Green', role: 'Architect', avatar: 'RG' },
        { name: 'Mark Davis', role: 'Electrical Engineer', avatar: 'MD' },
        { name: 'Kate Johnson', role: 'Safety Officer', avatar: 'KJ' },
        { name: 'Sam Wilson', role: 'Quality Inspector', avatar: 'SW' },
        { name: 'Maya Patel', role: 'Environmental Specialist', avatar: 'MP' },
        { name: 'Alex Turner', role: 'Structural Engineer', avatar: 'AT' },
        { name: 'Nina Rodriguez', role: 'Interior Designer', avatar: 'NR' },
        { name: 'Paul Anderson', role: 'HVAC Specialist', avatar: 'PA' },
        { name: 'Grace Kim', role: 'Smart Systems Engineer', avatar: 'GK' },
        { name: 'Robert Lee', role: 'Construction Worker', avatar: 'RL' }
      ],
      risks: [
        { 
          id: '1', 
          description: 'Weather delays during construction', 
          impact: 'medium', 
          probability: 'high',
          mitigation: 'Weather contingency plans and flexible scheduling'
        },
        { 
          id: '2', 
          description: 'Material supply chain disruptions', 
          impact: 'high', 
          probability: 'medium',
          mitigation: 'Multiple supplier agreements and inventory buffer'
        }
      ],
      recentActivities: [
        { 
          id: '1', 
          type: 'milestone', 
          description: 'Electrical & Plumbing milestone 60% complete', 
          date: '2024-01-17T16:45:00Z',
          user: 'Mark Davis'
        },
        { 
          id: '2', 
          type: 'inspection', 
          description: 'Safety inspection passed', 
          date: '2024-01-16T14:30:00Z',
          user: 'Kate Johnson'
        },
        { 
          id: '3', 
          type: 'delivery', 
          description: 'Smart building materials delivered', 
          date: '2024-01-15T09:15:00Z',
          user: 'Grace Kim'
        }
      ]
    },
    {
      id: '3',
      name: 'Safety Training Program',
      description: 'Comprehensive workplace safety training program development and implementation',
      client: 'BuildFast Construction',
      status: 'planning',
      priority: 'medium',
      progress: 25,
      budget: 85000.00,
      spent: 21250.00,
      remaining: 63750.00,
      currency: 'GBP',
      startDate: '2024-02-01T09:00:00Z',
      endDate: '2024-05-31T17:00:00Z',
      createdDate: '2024-01-10T10:00:00Z',
      updatedDate: '2024-01-16T11:30:00Z',
      projectManager: 'Emma Wilson',
      teamSize: 5,
      category: 'Training & Development',
      tags: ['training', 'safety', 'workplace', 'certification'],
      milestones: [
        { 
          id: '1', 
          name: 'Training Needs Assessment', 
          status: 'completed', 
          dueDate: '2024-02-15T17:00:00Z',
          completedDate: '2024-02-12T15:30:00Z',
          progress: 100 
        },
        { 
          id: '2', 
          name: 'Curriculum Development', 
          status: 'in-progress', 
          dueDate: '2024-03-15T17:00:00Z',
          completedDate: null,
          progress: 40 
        },
        { 
          id: '3', 
          name: 'Training Material Creation', 
          status: 'pending', 
          dueDate: '2024-04-15T17:00:00Z',
          completedDate: null,
          progress: 0 
        },
        { 
          id: '4', 
          name: 'Pilot Training Sessions', 
          status: 'pending', 
          dueDate: '2024-05-15T17:00:00Z',
          completedDate: null,
          progress: 0 
        },
        { 
          id: '5', 
          name: 'Full Program Launch', 
          status: 'pending', 
          dueDate: '2024-05-31T17:00:00Z',
          completedDate: null,
          progress: 0 
        }
      ],
      teamMembers: [
        { name: 'Emma Wilson', role: 'Project Manager', avatar: 'EW' },
        { name: 'Rachel Green', role: 'Training Specialist', avatar: 'RG' },
        { name: 'Mark Davis', role: 'Safety Expert', avatar: 'MD' },
        { name: 'Lisa Chen', role: 'Content Developer', avatar: 'LC' },
        { name: 'Tom Wilson', role: 'Assessment Designer', avatar: 'TW' }
      ],
      risks: [
        { 
          id: '1', 
          description: 'Low employee engagement in training', 
          impact: 'high', 
          probability: 'medium',
          mitigation: 'Interactive training methods and incentive programs'
        }
      ],
      recentActivities: [
        { 
          id: '1', 
          type: 'milestone', 
          description: 'Training Needs Assessment completed', 
          date: '2024-01-16T11:30:00Z',
          user: 'Rachel Green'
        },
        { 
          id: '2', 
          type: 'meeting', 
          description: 'Curriculum planning meeting held', 
          date: '2024-01-15T14:00:00Z',
          user: 'Emma Wilson'
        }
      ]
    },
    {
      id: '4',
      name: 'Digital Marketing Campaign',
      description: 'Comprehensive digital marketing campaign for luxury development project promotion',
      client: 'Urban Development Co',
      status: 'completed',
      priority: 'medium',
      progress: 100,
      budget: 45000.00,
      spent: 43200.00,
      remaining: 1800.00,
      currency: 'GBP',
      startDate: '2023-10-01T09:00:00Z',
      endDate: '2023-12-31T17:00:00Z',
      createdDate: '2023-09-15T10:00:00Z',
      updatedDate: '2024-01-05T09:00:00Z',
      projectManager: 'Lisa Chen',
      teamSize: 6,
      category: 'Marketing',
      tags: ['marketing', 'digital', 'luxury', 'campaign'],
      milestones: [
        { 
          id: '1', 
          name: 'Campaign Strategy', 
          status: 'completed', 
          dueDate: '2023-10-15T17:00:00Z',
          completedDate: '2023-10-12T16:00:00Z',
          progress: 100 
        },
        { 
          id: '2', 
          name: 'Content Creation', 
          status: 'completed', 
          dueDate: '2023-11-15T17:00:00Z',
          completedDate: '2023-11-10T14:30:00Z',
          progress: 100 
        },
        { 
          id: '3', 
          name: 'Campaign Launch', 
          status: 'completed', 
          dueDate: '2023-12-01T17:00:00Z',
          completedDate: '2023-11-28T10:00:00Z',
          progress: 100 
        },
        { 
          id: '4', 
          name: 'Performance Analysis', 
          status: 'completed', 
          dueDate: '2023-12-31T17:00:00Z',
          completedDate: '2023-12-29T15:45:00Z',
          progress: 100 
        }
      ],
      teamMembers: [
        { name: 'Lisa Chen', role: 'Project Manager', avatar: 'LC' },
        { name: 'Anna Lee', role: 'Marketing Strategist', avatar: 'AL' },
        { name: 'Chris Brown', role: 'Content Creator', avatar: 'CB' },
        { name: 'Maya Patel', role: 'Graphic Designer', avatar: 'MP' },
        { name: 'Sam Wilson', role: 'Social Media Manager', avatar: 'SW' },
        { name: 'Kate Johnson', role: 'Analytics Specialist', avatar: 'KJ' }
      ],
      risks: [],
      recentActivities: [
        { 
          id: '1', 
          type: 'completion', 
          description: 'Project completed successfully', 
          date: '2024-01-05T09:00:00Z',
          user: 'Lisa Chen'
        },
        { 
          id: '2', 
          type: 'report', 
          description: 'Final performance report delivered', 
          date: '2024-01-03T16:30:00Z',
          user: 'Kate Johnson'
        }
      ]
    },
    {
      id: '5',
      name: 'Legacy System Migration',
      description: 'Complete legacy system modernization and cloud migration project',
      client: 'Construct Plus',
      status: 'on-hold',
      priority: 'low',
      progress: 15,
      budget: 180000.00,
      spent: 27000.00,
      remaining: 153000.00,
      currency: 'GBP',
      startDate: '2024-01-15T09:00:00Z',
      endDate: '2024-12-31T17:00:00Z',
      createdDate: '2023-12-01T10:00:00Z',
      updatedDate: '2024-01-15T14:20:00Z',
      projectManager: 'Mike Chen',
      teamSize: 4,
      category: 'System Migration',
      tags: ['migration', 'legacy', 'cloud', 'modernization'],
      milestones: [
        { 
          id: '1', 
          name: 'System Analysis', 
          status: 'completed', 
          dueDate: '2024-02-15T17:00:00Z',
          completedDate: '2024-02-10T16:30:00Z',
          progress: 100 
        },
        { 
          id: '2', 
          name: 'Migration Planning', 
          status: 'in-progress', 
          dueDate: '2024-04-15T17:00:00Z',
          completedDate: null,
          progress: 30 
        },
        { 
          id: '3', 
          name: 'Data Migration', 
          status: 'pending', 
          dueDate: '2024-08-15T17:00:00Z',
          completedDate: null,
          progress: 0 
        },
        { 
          id: '4', 
          name: 'System Testing', 
          status: 'pending', 
          dueDate: '2024-11-15T17:00:00Z',
          completedDate: null,
          progress: 0 
        },
        { 
          id: '5', 
          name: 'Go-Live', 
          status: 'pending', 
          dueDate: '2024-12-31T17:00:00Z',
          completedDate: null,
          progress: 0 
        }
      ],
      teamMembers: [
        { name: 'Mike Chen', role: 'Project Manager', avatar: 'MC' },
        { name: 'David Brown', role: 'System Architect', avatar: 'DB' },
        { name: 'Tom Wilson', role: 'Migration Specialist', avatar: 'TW' },
        { name: 'Anna Lee', role: 'Data Analyst', avatar: 'AL' }
      ],
      risks: [
        { 
          id: '1', 
          description: 'Client budget constraints', 
          impact: 'high', 
          probability: 'high',
          mitigation: 'Project on hold pending budget approval'
        },
        { 
          id: '2', 
          description: 'Legacy system complexity', 
          impact: 'medium', 
          probability: 'high',
          mitigation: 'Extended analysis phase and phased migration approach'
        }
      ],
      recentActivities: [
        { 
          id: '1', 
          type: 'status', 
          description: 'Project placed on hold due to budget constraints', 
          date: '2024-01-15T14:20:00Z',
          user: 'Mike Chen'
        },
        { 
          id: '2', 
          type: 'meeting', 
          description: 'Budget review meeting with client', 
          date: '2024-01-12T11:00:00Z',
          user: 'Mike Chen'
        }
      ]
    }
  ]);

  // Project analytics
  const [projectAnalytics, setProjectAnalytics] = useState({
    totalProjects: 5,
    activeProjects: 3,
    completedProjects: 1,
    onHoldProjects: 1,
    totalBudget: 720000.00,
    totalSpent: 333950.00,
    totalRemaining: 386050.00,
    avgProgress: 50.0,
    onTimeProjects: 4,
    delayedProjects: 1
  });

  // Project insights
  const [projectInsights, setProjectInsights] = useState([
    {
      id: '1',
      type: 'achievement',
      title: 'Strong Project Completion Rate',
      description: '80% of projects are on track or completed successfully',
      impact: 'high',
      action: 'Continue current project management practices',
      icon: 'trophy',
      color: '#10B981'
    },
    {
      id: '2',
      type: 'concern',
      title: 'Budget Utilization Analysis',
      description: 'One project on hold due to budget constraints affecting overall portfolio',
      impact: 'medium',
      action: 'Review budget allocation and client communication strategies',
      icon: 'alert-circle',
      color: '#F59E0B'
    },
    {
      id: '3',
      type: 'opportunity',
      title: 'Team Utilization Optimization',
      description: 'Average team size of 7 members shows good resource allocation',
      impact: 'medium',
      action: 'Consider cross-project resource sharing for efficiency',
      icon: 'trending-up',
      color: '#3B82F6'
    },
    {
      id: '4',
      type: 'recommendation',
      title: 'Risk Management Excellence',
      description: 'Proactive risk identification and mitigation strategies in place',
      impact: 'high',
      action: 'Share risk management best practices across all projects',
      icon: 'lightbulb',
      color: '#8B5CF6'
    }
  ]);

  // Team performance
  const [teamPerformance, setTeamPerformance] = useState([
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Senior Project Manager',
      activeProjects: 1,
      completedProjects: 8,
      onTimeDelivery: 95.5,
      budgetAccuracy: 92.3,
      teamSatisfaction: 4.8,
      avatar: 'SJ',
      performance: 'excellent'
    },
    {
      id: '2',
      name: 'David Brown',
      role: 'Project Manager',
      activeProjects: 1,
      completedProjects: 5,
      onTimeDelivery: 88.2,
      budgetAccuracy: 89.7,
      teamSatisfaction: 4.5,
      avatar: 'DB',
      performance: 'good'
    },
    {
      id: '3',
      name: 'Emma Wilson',
      role: 'Project Manager',
      activeProjects: 1,
      completedProjects: 3,
      onTimeDelivery: 91.0,
      budgetAccuracy: 94.1,
      teamSatisfaction: 4.6,
      avatar: 'EW',
      performance: 'excellent'
    },
    {
      id: '4',
      name: 'Lisa Chen',
      role: 'Project Manager',
      activeProjects: 0,
      completedProjects: 4,
      onTimeDelivery: 96.8,
      budgetAccuracy: 98.2,
      teamSatisfaction: 4.9,
      avatar: 'LC',
      performance: 'excellent'
    },
    {
      id: '5',
      name: 'Mike Chen',
      role: 'Technical Project Manager',
      activeProjects: 1,
      completedProjects: 2,
      onTimeDelivery: 75.0,
      budgetAccuracy: 85.5,
      teamSatisfaction: 4.2,
      avatar: 'MC',
      performance: 'needs-improvement'
    }
  ]);

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      // Simulate loading data
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load project data');
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-GB').format(number);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': '#F59E0B',
      'in-progress': '#3B82F6',
      'on-hold': '#EF4444',
      'completed': '#10B981',
      'cancelled': '#6B7280',
      'pending': '#9CA3AF'
    };
    return colors[status] || '#6B7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#10B981',
      'medium': '#F59E0B',
      'high': '#EF4444',
      'critical': '#7C2D12'
    };
    return colors[priority] || '#6B7280';
  };

  const getImpactColor = (impact) => {
    const colors = {
      'high': '#EF4444',
      'medium': '#F59E0B',
      'low': '#10B981'
    };
    return colors[impact] || '#6B7280';
  };

  const getPerformanceColor = (performance) => {
    const colors = {
      'excellent': '#10B981',
      'good': '#3B82F6',
      'needs-improvement': '#F59E0B',
      'poor': '#EF4444'
    };
    return colors[performance] || '#6B7280';
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = String(project.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(project.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(project.client ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || project.priority === filterPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const addProject = () => {
    Alert.alert(
      'Add Project',
      'Project creation functionality would be implemented here.',
      [{ text: 'OK' }]
    );
  };

  const editProject = (projectId) => {
    Alert.alert(
      'Edit Project',
      `Edit functionality for project ${projectId} would be implemented here.`,
      [{ text: 'OK' }]
    );
  };

  const deleteProject = (projectId) => {
    Alert.alert(
      'Delete Project',
      'Are you sure you want to delete this project?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            setProjects(projects.filter(p => p.id !== projectId));
            Alert.alert('Success', 'Project deleted successfully');
          }
        }
      ]
    );
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#1F2937', '#374151']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Project Management</Text>
          <Text style={styles.headerSubtitle}>Manage Projects & Teams</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.headerButton}
          onPress={addProject}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'projects', title: 'Projects', icon: 'briefcase' },
          { key: 'timeline', title: 'Timeline', icon: 'timeline' },
          { key: 'team', title: 'Team Performance', icon: 'account-group' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-line' },
          { key: 'insights', title: 'Insights', icon: 'lightbulb' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchAndFilter = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search projects..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        <Text style={styles.filterLabel}>Status:</Text>
        {projectStatuses.map((status) => (
          <TouchableOpacity
            key={status}
            style={[
              styles.filterButton,
              filterStatus === status && styles.activeFilterButton
            ]}
            onPress={() => setFilterStatus(status)}
          >
            <Text style={[
              styles.filterButtonText,
              filterStatus === status && styles.activeFilterButtonText
            ]}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        <Text style={styles.filterLabel}>Priority:</Text>
        {projectPriorities.map((priority) => (
          <TouchableOpacity
            key={priority}
            style={[
              styles.filterButton,
              filterPriority === priority && styles.activeFilterButton
            ]}
            onPress={() => setFilterPriority(priority)}
          >
            <Text style={[
              styles.filterButtonText,
              filterPriority === priority && styles.activeFilterButtonText
            ]}>
              {priority.charAt(0).toUpperCase() + priority.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderProjectItem = ({ item }) => (
    <View style={styles.projectCard}>
      <View style={styles.projectHeader}>
        <View style={styles.projectInfo}>
          <Text style={styles.projectName}>{item.name}</Text>
          <Text style={styles.projectClient}>{item.client}</Text>
          <Text style={styles.projectCategory}>{item.category}</Text>
        </View>
        <View style={styles.projectBadges}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusBadgeText}>{item.status}</Text>
          </View>
          <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(item.priority) }]}>
            <Text style={styles.priorityBadgeText}>{item.priority}</Text>
          </View>
        </View>
      </View>
      
      <Text style={styles.projectDescription}>{item.description}</Text>
      
      <View style={styles.projectProgress}>
        <View style={styles.projectProgressHeader}>
          <Text style={styles.projectProgressLabel}>Progress</Text>
          <Text style={styles.projectProgressValue}>{item.progress}%</Text>
        </View>
        <View style={styles.progressBar}>
          <View 
            style={[
              styles.progressFill, 
              { 
                width: `${item.progress}%`,
                backgroundColor: getStatusColor(item.status)
              }
            ]} 
          />
        </View>
      </View>
      
      <View style={styles.projectMetrics}>
        <View style={styles.projectMetricItem}>
          <MaterialCommunityIcons name="currency-usd" size={16} color="#10B981" />
          <Text style={styles.projectMetricLabel}>Budget</Text>
          <Text style={styles.projectMetricValue}>{formatCurrency(item.budget)}</Text>
        </View>
        <View style={styles.projectMetricItem}>
          <MaterialCommunityIcons name="account-group" size={16} color="#3B82F6" />
          <Text style={styles.projectMetricLabel}>Team</Text>
          <Text style={styles.projectMetricValue}>{item.teamSize}</Text>
        </View>
        <View style={styles.projectMetricItem}>
          <MaterialCommunityIcons name="calendar" size={16} color="#F59E0B" />
          <Text style={styles.projectMetricLabel}>Duration</Text>
          <Text style={styles.projectMetricValue}>
            {Math.ceil((new Date(item.endDate) - new Date(item.startDate)) / (1000 * 60 * 60 * 24))} days
          </Text>
        </View>
      </View>
      
      <View style={styles.projectBudget}>
        <View style={styles.projectBudgetItem}>
          <Text style={styles.projectBudgetLabel}>Spent</Text>
          <Text style={styles.projectBudgetValue}>{formatCurrency(item.spent)}</Text>
        </View>
        <View style={styles.projectBudgetItem}>
          <Text style={styles.projectBudgetLabel}>Remaining</Text>
          <Text style={[styles.projectBudgetValue, { color: '#10B981' }]}>
            {formatCurrency(item.remaining)}
          </Text>
        </View>
        <View style={styles.projectBudgetItem}>
          <Text style={styles.projectBudgetLabel}>Manager</Text>
          <Text style={styles.projectBudgetValue}>{item.projectManager}</Text>
        </View>
      </View>
      
      <View style={styles.projectTags}>
        {item.tags.slice(0, 3).map((tag, index) => (
          <View key={index} style={styles.projectTag}>
            <Text style={styles.projectTagText}>{tag}</Text>
          </View>
        ))}
        {item.tags.length > 3 && (
          <View style={styles.projectTag}>
            <Text style={styles.projectTagText}>+{item.tags.length - 3}</Text>
          </View>
        )}
      </View>
      
      <View style={styles.projectDates}>
        <Text style={styles.projectDate}>
          Started: {formatDate(item.startDate)}
        </Text>
        <Text style={styles.projectDate}>
          Due: {formatDate(item.endDate)}
        </Text>
      </View>
      
      <View style={styles.projectActions}>
        <TouchableOpacity 
          style={styles.projectActionButton}
          onPress={() => {
            setSelectedProject(item);
            setShowProjectModal(true);
          }}
        >
          <MaterialCommunityIcons name="eye" size={16} color="#3B82F6" />
          <Text style={styles.projectActionButtonText}>View</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.projectActionButton}
          onPress={() => editProject(item.id)}
        >
          <MaterialCommunityIcons name="pencil" size={16} color="#10B981" />
          <Text style={styles.projectActionButtonText}>Edit</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.projectActionButton}
          onPress={() => deleteProject(item.id)}
        >
          <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
          <Text style={styles.projectActionButtonText}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTimelineItem = ({ item }) => (
    <View style={styles.timelineCard}>
      <View style={styles.timelineHeader}>
        <Text style={styles.timelineName}>{item.name}</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusBadgeText}>{item.status}</Text>
        </View>
      </View>
      
      <Text style={styles.timelineClient}>{item.client}</Text>
      
      <View style={styles.timelineDates}>
        <Text style={styles.timelineDate}>
          Start: {formatDate(item.startDate)}
        </Text>
        <Text style={styles.timelineDate}>
          End: {formatDate(item.endDate)}
        </Text>
      </View>
      
      <View style={styles.timelineProgress}>
        <View style={styles.progressBar}>
          <View 
            style={[
              styles.progressFill, 
              { 
                width: `${item.progress}%`,
                backgroundColor: getStatusColor(item.status)
              }
            ]} 
          />
        </View>
        <Text style={styles.timelineProgressText}>{item.progress}%</Text>
      </View>
      
      <View style={styles.timelineMilestones}>
        <Text style={styles.timelineMilestonesTitle}>Milestones ({item.milestones.length})</Text>
        {item.milestones.slice(0, 3).map((milestone, index) => (
          <View key={index} style={styles.timelineMilestone}>
            <MaterialCommunityIcons 
              name={milestone.status === 'completed' ? 'check-circle' : 
                    milestone.status === 'in-progress' ? 'clock' : 'circle-outline'} 
              size={16} 
              color={milestone.status === 'completed' ? '#10B981' : 
                     milestone.status === 'in-progress' ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={styles.timelineMilestoneText}>{milestone.name}</Text>
          </View>
        ))}
        {item.milestones.length > 3 && (
          <Text style={styles.timelineMilestonesMore}>+{item.milestones.length - 3} more</Text>
        )}
      </View>
    </View>
  );

  const renderTeamItem = ({ item }) => (
    <View style={styles.teamCard}>
      <View style={styles.teamHeader}>
        <View style={styles.teamAvatar}>
          <Text style={styles.teamAvatarText}>{item.avatar}</Text>
        </View>
        <View style={styles.teamInfo}>
          <Text style={styles.teamName}>{item.name}</Text>
          <Text style={styles.teamRole}>{item.role}</Text>
        </View>
        <View style={[styles.performanceBadge, { backgroundColor: getPerformanceColor(item.performance) }]}>
          <Text style={styles.performanceBadgeText}>{item.performance}</Text>
        </View>
      </View>
      
      <View style={styles.teamMetrics}>
        <View style={styles.teamMetricItem}>
          <Text style={styles.teamMetricLabel}>Active Projects</Text>
          <Text style={styles.teamMetricValue}>{item.activeProjects}</Text>
        </View>
        <View style={styles.teamMetricItem}>
          <Text style={styles.teamMetricLabel}>Completed</Text>
          <Text style={styles.teamMetricValue}>{item.completedProjects}</Text>
        </View>
        <View style={styles.teamMetricItem}>
          <Text style={styles.teamMetricLabel}>On-Time Delivery</Text>
          <Text style={styles.teamMetricValue}>{formatPercentage(item.onTimeDelivery)}</Text>
        </View>
        <View style={styles.teamMetricItem}>
          <Text style={styles.teamMetricLabel}>Budget Accuracy</Text>
          <Text style={styles.teamMetricValue}>{formatPercentage(item.budgetAccuracy)}</Text>
        </View>
        <View style={styles.teamMetricItem}>
          <Text style={styles.teamMetricLabel}>Team Satisfaction</Text>
          <Text style={styles.teamMetricValue}>{item.teamSatisfaction}/5</Text>
        </View>
      </View>
    </View>
  );

  const renderAnalytics = () => (
    <ScrollView style={styles.analyticsContainer}>
      <View style={styles.analyticsOverview}>
        <Text style={styles.analyticsTitle}>Project Analytics</Text>
        
        <View style={styles.analyticsGrid}>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="briefcase" size={24} color="#3B82F6" />
            <Text style={styles.analyticsValue}>{projectAnalytics.totalProjects}</Text>
            <Text style={styles.analyticsLabel}>Total Projects</Text>
          </View>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="play-circle" size={24} color="#10B981" />
            <Text style={styles.analyticsValue}>{projectAnalytics.activeProjects}</Text>
            <Text style={styles.analyticsLabel}>Active Projects</Text>
          </View>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="check-circle" size={24} color="#F59E0B" />
            <Text style={styles.analyticsValue}>{projectAnalytics.completedProjects}</Text>
            <Text style={styles.analyticsLabel}>Completed</Text>
          </View>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="pause-circle" size={24} color="#EF4444" />
            <Text style={styles.analyticsValue}>{projectAnalytics.onHoldProjects}</Text>
            <Text style={styles.analyticsLabel}>On Hold</Text>
          </View>
        </View>
        
        <View style={styles.analyticsMetrics}>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Total Budget</Text>
            <Text style={styles.analyticsMetricValue}>{formatCurrency(projectAnalytics.totalBudget)}</Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Total Spent</Text>
            <Text style={styles.analyticsMetricValue}>{formatCurrency(projectAnalytics.totalSpent)}</Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Remaining Budget</Text>
            <Text style={[styles.analyticsMetricValue, { color: '#10B981' }]}>
              {formatCurrency(projectAnalytics.totalRemaining)}
            </Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Average Progress</Text>
            <Text style={styles.analyticsMetricValue}>{formatPercentage(projectAnalytics.avgProgress)}</Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>On-Time Projects</Text>
            <Text style={styles.analyticsMetricValue}>{projectAnalytics.onTimeProjects}</Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Delayed Projects</Text>
            <Text style={[styles.analyticsMetricValue, { color: '#EF4444' }]}>
              {projectAnalytics.delayedProjects}
            </Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderInsightItem = ({ item }) => (
    <View style={styles.insightCard}>
      <View style={styles.insightHeader}>
        <View style={styles.insightIcon}>
          <MaterialCommunityIcons 
            name={item.icon} 
            size={24} 
            color={item.color} 
          />
        </View>
        <View style={styles.insightInfo}>
          <Text style={styles.insightTitle}>{item.title}</Text>
          <View style={[styles.impactBadge, { backgroundColor: getImpactColor(item.impact) }]}>
            <Text style={styles.impactBadgeText}>{item.impact} impact</Text>
          </View>
        </View>
      </View>
      
      <Text style={styles.insightDescription}>{item.description}</Text>
      
      <View style={styles.insightAction}>
        <MaterialCommunityIcons name="lightbulb" size={16} color="#F59E0B" />
        <Text style={styles.insightActionText}>{item.action}</Text>
      </View>
    </View>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'projects':
        return (
          <View style={styles.projectsContainer}>
            {renderSearchAndFilter()}
            <FlatList
              data={filteredProjects}
              keyExtractor={(item) => item.id}
              renderItem={renderProjectItem}
              contentContainerStyle={styles.listContainer}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                  colors={['#3B82F6']}
                />
              }
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <MaterialCommunityIcons name="briefcase-off" size={64} color="#D1D5DB" />
                  <Text style={styles.emptyTitle}>No projects found</Text>
                  <Text style={styles.emptySubtitle}>
                    <Text>{searchQuery ? 'Try adjusting your search criteria' : 'Add your first project to get started'}</Text>
                  </Text>
                </View>
              }
            />
          </View>
        );
      case 'timeline':
        return (
          <FlatList
            data={projects}
            keyExtractor={(item) => item.id}
            renderItem={renderTimelineItem}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#3B82F6']}
              />
            }
            showsVerticalScrollIndicator={false}
          />
        );
      case 'team':
        return (
          <FlatList
            data={teamPerformance}
            keyExtractor={(item) => item.id}
            renderItem={renderTeamItem}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#3B82F6']}
              />
            }
            showsVerticalScrollIndicator={false}
          />
        );
      case 'analytics':
        return renderAnalytics();
      case 'insights':
        return (
          <FlatList
            data={projectInsights}
            keyExtractor={(item) => item.id}
            renderItem={renderInsightItem}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#3B82F6']}
              />
            }
            showsVerticalScrollIndicator={false}
          />
        );
      default:
        return null;
    }
  };

  const renderProjectModal = () => (
    <Modal
      visible={showProjectModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => {
        setShowProjectModal(false);
        setSelectedProject(null);
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {selectedProject ? selectedProject.name : 'Project Details'}
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => {
              setShowProjectModal(false);
              setSelectedProject(null);
            }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        {selectedProject && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Project Information</Text>
              <Text style={styles.modalText}>
                Client: {selectedProject.client}{'\n'}
                Category: {selectedProject.category}{'\n'}
                Status: {selectedProject.status}{'\n'}
                Priority: {selectedProject.priority}{'\n'}
                Progress: {selectedProject.progress}%{'\n'}
                Project Manager: {selectedProject.projectManager}{'\n'}
                Team Size: {selectedProject.teamSize} members{'\n'}
                Created: {formatDate(selectedProject.createdDate)}{'\n'}
                Updated: {formatDate(selectedProject.updatedDate)}
              </Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Description</Text>
              <Text style={styles.modalText}>{selectedProject.description}</Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Budget & Financials</Text>
              <Text style={styles.modalText}>
                Total Budget: {formatCurrency(selectedProject.budget)}{'\n'}
                Amount Spent: {formatCurrency(selectedProject.spent)}{'\n'}
                Remaining: {formatCurrency(selectedProject.remaining)}{'\n'}
                Budget Utilization: {formatPercentage((selectedProject.spent / selectedProject.budget) * 100)}
              </Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Timeline</Text>
              <Text style={styles.modalText}>
                Start Date: {formatDate(selectedProject.startDate)}{'\n'}
                End Date: {formatDate(selectedProject.endDate)}{'\n'}
                Duration: {Math.ceil((new Date(selectedProject.endDate) - new Date(selectedProject.startDate)) / (1000 * 60 * 60 * 24))} days
              </Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Milestones</Text>
              {selectedProject.milestones.map((milestone, index) => (
                <View key={index} style={styles.modalMilestone}>
                  <MaterialCommunityIcons 
                    name={milestone.status === 'completed' ? 'check-circle' : 
                          milestone.status === 'in-progress' ? 'clock' : 'circle-outline'} 
                    size={16} 
                    color={milestone.status === 'completed' ? '#10B981' : 
                           milestone.status === 'in-progress' ? '#3B82F6' : '#6B7280'} 
                  />
                  <View style={styles.modalMilestoneInfo}>
                    <Text style={styles.modalMilestoneText}>{milestone.name}</Text>
                    <Text style={styles.modalMilestoneDate}>
                      Due: {formatDate(milestone.dueDate)}
                      {milestone.completedDate && ` | Completed: ${formatDate(milestone.completedDate)}`}
                    </Text>
                    <Text style={styles.modalMilestoneProgress}>Progress: {milestone.progress}%</Text>
                  </View>
                </View>
              ))}
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Team Members</Text>
              {selectedProject.teamMembers.map((member, index) => (
                <View key={index} style={styles.modalTeamMember}>
                  <View style={styles.modalTeamAvatar}>
                    <Text style={styles.modalTeamAvatarText}>{member.avatar}</Text>
                  </View>
                  <View style={styles.modalTeamInfo}>
                    <Text style={styles.modalTeamName}>{member.name}</Text>
                    <Text style={styles.modalTeamRole}>{member.role}</Text>
                  </View>
                </View>
              ))}
            </View>
            
            {selectedProject.risks.length > 0 && (
              <View style={styles.modalSection}>
                <Text style={styles.modalSectionTitle}>Risks & Mitigation</Text>
                {selectedProject.risks.map((risk, index) => (
                  <View key={index} style={styles.modalRisk}>
                    <Text style={styles.modalRiskDescription}>{risk.description}</Text>
                    <Text style={styles.modalRiskDetails}>
                      Impact: {risk.impact} | Probability: {risk.probability}
                    </Text>
                    <Text style={styles.modalRiskMitigation}>
                      Mitigation: {risk.mitigation}
                    </Text>
                  </View>
                ))}
              </View>
            )}
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Recent Activities</Text>
              {selectedProject.recentActivities.map((activity, index) => (
                <View key={index} style={styles.modalActivity}>
                  <MaterialCommunityIcons 
                    name={activity.type === 'milestone' ? 'flag' : 
                          activity.type === 'meeting' ? 'account-group' : 
                          activity.type === 'update' ? 'update' : 'information'} 
                    size={16} 
                    color="#3B82F6" 
                  />
                  <View style={styles.modalActivityInfo}>
                    <Text style={styles.modalActivityText}>{activity.description}</Text>
                    <Text style={styles.modalActivityDate}>
                      {formatDate(activity.date)} by {activity.user}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Tags</Text>
              <View style={styles.modalTags}>
                {selectedProject.tags.map((tag, index) => (
                  <View key={index} style={styles.modalTag}>
                    <Text style={styles.modalTagText}>{tag}</Text>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading Project Data...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderProjectModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 140,
    paddingVertical: 12,
    marginRight: 4,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 6,
    fontWeight: '600',
  },
  
  activeTabText: {
    color: '#3B82F6',
  },
  
  // Search and filter styles
  searchContainer: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
    marginLeft: 12,
  },
  
  filterContainer: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginRight: 12,
  },
  
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  activeFilterButton: {
    backgroundColor: '#EBF4FF',
    borderColor: '#3B82F6',
  },
  
  filterButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  activeFilterButtonText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // List styles
  listContainer: {
    padding: 16,
  },
  
  projectsContainer: {
    flex: 1,
  },
  
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 64,
  },
  
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
  },
  
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  
  // Project card styles
  projectCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  projectHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  projectInfo: {
    flex: 1,
  },
  
  projectName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectClient: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  
  projectCategory: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  
  projectBadges: {
    alignItems: 'flex-end',
  },
  
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  
  statusBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  priorityBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  projectDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 16,
  },
  
  projectProgress: {
    marginBottom: 16,
  },
  
  projectProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  projectProgressLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectProgressValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
  },
  
  progressBar: {
    height: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
  },
  
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  
  projectMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  projectMetricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  
  projectMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 4,
    marginRight: 4,
  },
  
  projectMetricValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectBudget: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  
  projectBudgetItem: {
    alignItems: 'center',
  },
  
  projectBudgetLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  projectBudgetValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  
  projectTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  
  projectTagText: {
    fontSize: 10,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  projectDates: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  
  projectDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  projectActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  
  projectActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    flex: 1,
    marginHorizontal: 4,
    justifyContent: 'center',
  },
  
  projectActionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  
  // Timeline card styles
  timelineCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  timelineHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  
  timelineName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  
  timelineClient: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 12,
  },
  
  timelineDates: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  timelineDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  timelineProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  
  timelineProgressText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 8,
  },
  
  timelineMilestones: {
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  
  timelineMilestonesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  timelineMilestone: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  
  timelineMilestoneText: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 8,
  },
  
  timelineMilestonesMore: {
    fontSize: 12,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginTop: 4,
  },
  
  // Team card styles
  teamCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  teamHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  
  teamAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  teamAvatarText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  teamInfo: {
    flex: 1,
    marginLeft: 12,
  },
  
  teamName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  teamRole: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  
  performanceBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  performanceBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  teamMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  
  teamMetricItem: {
    width: (width - 64) / 2,
    marginBottom: 12,
  },
  
  teamMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  teamMetricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Analytics styles
  analyticsContainer: {
    padding: 16,
  },
  
  analyticsOverview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  analyticsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 20,
  },
  
  analyticsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  
  analyticsItem: {
    width: (width - 64) / 2,
    alignItems: 'center',
    marginBottom: 16,
  },
  
  analyticsValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
    textAlign: 'center',
  },
  
  analyticsLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
    textAlign: 'center',
  },
  
  analyticsMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 16,
  },
  
  analyticsMetricItem: {
    width: (width - 64) / 2,
    marginBottom: 12,
  },
  
  analyticsMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  analyticsMetricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Insight card styles
  insightCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  insightIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  insightInfo: {
    flex: 1,
    marginLeft: 12,
  },
  
  insightTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  impactBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  
  impactBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  insightDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 12,
  },
  
  insightAction: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    padding: 12,
    borderRadius: 8,
  },
  
  insightActionText: {
    flex: 1,
    fontSize: 14,
    color: '#92400E',
    marginLeft: 8,
    lineHeight: 20,
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalSection: {
    marginBottom: 24,
  },
  
  modalSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  modalText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 4,
  },
  
  modalMilestone: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  modalMilestoneInfo: {
    flex: 1,
    marginLeft: 8,
  },
  
  modalMilestoneText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  modalMilestoneDate: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  
  modalMilestoneProgress: {
    fontSize: 12,
    color: '#3B82F6',
    marginTop: 2,
  },
  
  modalTeamMember: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  modalTeamAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalTeamAvatarText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  modalTeamInfo: {
    marginLeft: 12,
  },
  
  modalTeamName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  modalTeamRole: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  
  modalRisk: {
    backgroundColor: '#FEF2F2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  
  modalRiskDescription: {
    fontSize: 14,
    fontWeight: '600',
    color: '#991B1B',
    marginBottom: 4,
  },
  
  modalRiskDetails: {
    fontSize: 12,
    color: '#7F1D1D',
    marginBottom: 4,
  },
  
  modalRiskMitigation: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  
  modalActivity: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  modalActivityInfo: {
    flex: 1,
    marginLeft: 8,
  },
  
  modalActivityText: {
    fontSize: 14,
    color: '#1F2937',
  },
  
  modalActivityDate: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  
  modalTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  
  modalTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  
  modalTagText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
});